import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftOutlined from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import { Button, Grid, Modal, Tab, Tabs, Typography } from '@mui/material';
import MuiCircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import type { FetcherWithComponents } from '@remix-run/react';
import { useFetcher, useLocation, useNavigation, useRouteLoaderData, useSubmit } from '@remix-run/react';
import * as React from 'react';
import Image from 'remix-image';
import { ReadOnlySize } from '~/components/ProductDetails/Attributes';
import Colors, { SelectedColor } from '~/components/shared/Colors';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { LoaderData } from '~/root';
import { PricesBreakdown, QuantityAndPrice, getConfigurationType } from '~/routes/__contained/shop/$productSlug/index';
import type { Attribute, Color } from '~/services/api-utils.server';
import type {
  CartItem,
  ConfigurationType,
  ItemAttributes,
  PricesBreakdownEstimates,
  Product,
} from '~/services/api.server';
import {
  getAttributesId,
  isApproved,
  isCartItemProduct,
  isCustomPackOrProduct,
  isPackOrProductPOD,
  isSupplierProduct,
} from '~/utils/common';
import * as gtm from '~/utils/gtm.client';
import SeeMoreDescription from './SeeMoreDescription';
import Sizes from './Sizes';
import { MuiRemixLink } from './links';

function constructURLwithSearchParams(
  basePath: string,
  productSlug: string,
  attributes?: Array<{ [key: string]: string | number }>
) {
  const params = new URLSearchParams();
  params.set('productSlug', productSlug);

  if (attributes && attributes.length > 0) {
    for (let i = 0; i < attributes.length; i++) {
      const element = attributes[i];
      if (Object.values(element)[0]) {
        params.set(Object.keys(element)[0].toString(), Object.values(element)[0].toString());
      }
    }
  }

  const finalUrl = basePath + '?' + params.toString();

  return finalUrl;
}

function getUnavaliableSizes(product: Product, selectedColor: string) {
  return product.unavailable_variants.reduce(function (unavaliableSizes: string[], variant) {
    const colorSize = variant.split(',');
    if (selectedColor == colorSize[0]) {
      unavaliableSizes.push(colorSize[1]);
    }
    return unavaliableSizes;
  }, []);
}

export default function QuickShopModal({
  product,
  isAuthenticated,
  onClose,
}: {
  product: Product | null;
  isAuthenticated: boolean | undefined;
  onClose: () => void;
}) {
  return (
    <Modal
      open={!!product}
      onClose={onClose}
      sx={{
        display: 'flex',
        overflowY: { xs: 'auto' },
        alignItems: { md: 'center' },
        justifyContent: 'center',
      }}
      keepMounted
    >
      <>{product && <ProductForm product={product} isAuthenticated={isAuthenticated} onClose={onClose} />}</>
    </Modal>
  );
}

const initialQuantity = 100;

function ProductForm({
  product,
  isAuthenticated,
  onClose,
}: {
  product: Product;
  isAuthenticated: boolean | undefined;
  onClose: () => void;
}) {
  const fetcher = useFetcher();
  const submit = useSubmit();
  const navigation = useNavigation();

  const rootData = useRouteLoaderData('root') as LoaderData;
  const currentCart = rootData?.cart;
  const { pathname } = useLocation();

  const imagesFetcher = useFetcher();
  const priceFetcher = useFetcher();

  const isSafsiraProduct = product.safsira_connected_product;

  const canBeDesigned =
    (product.design_lab_ready && !isCustomPackOrProduct(product)) ||
    (isPackOrProductPOD(product) && isApproved(product.design_status));

  const [mainImage, setMainImage] = React.useState<string>();
  const [secondaryImages, setSecondaryImages] = React.useState<Array<string>>([]);
  const [currentPage, setCurrentPage] = React.useState(1);

  const [selectedColor, setSelectedColor] = React.useState<string>(product.available_attributes.colors?.[0].name);
  const [selectedSize, setSelectedSize] = React.useState<string>(product.available_attributes.sizes?.[0].name);
  const [selectedBookType, setSelectedBookType] = React.useState<string>(
    product.available_attributes.book_types?.[0].name
  );

  const [tab, setTab] = React.useState(0);
  const [prices, setPrices] = React.useState<number>(0);
  const [pricesBreakdown, setPricesBreakdown] = React.useState<any>(null);

  const currentCartProduct = currentCart.products.find((p: CartItem) =>
    hasCartItemSameAttributes(p, {
      configuration_type: getConfigurationType(tab),
      productId: product.id,
      itemAttributes: getProductAttributes(product),
    })
  );
  const currentPart = currentCartProduct?.parts.find((part) => {
    const packAttributes = getProductAttributes(product);
    if ('items' in part) {
      return getAttributesId(part.items) === getAttributesId(packAttributes);
    }

    return (
      getAttributesId([{ product_id: currentCartProduct.product.id, attributes: part.attributes }]) ===
      getAttributesId(packAttributes)
    );
  });

  const maxQuantity =
    tab === 2 ? product.max_sample_quantity : imagesFetcher.data?.supplier_inventories?.[0]?.quantity || undefined;

  const getInitialQuantity = React.useCallback(() => {
    if (currentPart?.quantity) return currentPart.quantity;
    if (maxQuantity === undefined) return initialQuantity;
    return maxQuantity > initialQuantity ? initialQuantity : maxQuantity;
  }, [currentPart?.quantity, maxQuantity]);

  const [quantityChanged, setQuantityChanged] = React.useState(false);
  const [quantity, setQuantity] = React.useState<number | null>(getInitialQuantity);
  const productHasMoreThanOneColorAndHasSizes =
    product.available_attributes?.['colors']?.length > 1 && Boolean(product.available_attributes?.['sizes']);
  const [selectColors, setSelectColors] = React.useState(productHasMoreThanOneColorAndHasSizes);

  React.useEffect(() => {
    if (imagesFetcher.type === 'init') {
      imagesFetcher.load(
        constructURLwithSearchParams(`/api/product-part-images`, product.slug, [{ color: selectedColor }])
      );
    }
  }, [imagesFetcher, product.slug, selectedColor]);

  React.useEffect(() => {
    const _quantity = getInitialQuantity();
    setQuantity(_quantity);
  }, [getInitialQuantity]);

  React.useEffect(() => {
    setQuantityChanged(true);
  }, [quantity]);

  const loadPrice = priceFetcher.load;
  const loadImage = imagesFetcher.load;
  React.useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (quantity) {
      setQuantityChanged(false);
      timeout = setTimeout(() => {
        loadPrice(
          constructURLwithSearchParams(`/api/product-prices`, product.slug, [
            { quantity },
            { color: selectedColor },
            { size: selectedSize },
            { book_type: selectedBookType },
            { configuration_type: getConfigurationType(tab) },
          ])
        );
        loadImage(
          constructURLwithSearchParams(`/api/product-part-images`, product.slug, [
            { color: selectedColor },
            { size: selectedSize },
            { book_type: selectedBookType },
          ])
        );
      }, 750);
    }
    return () => clearTimeout(timeout);
  }, [loadImage, loadPrice, product.slug, quantity, selectedBookType, selectedColor, selectedSize, tab]);

  React.useEffect(() => {
    if (imagesFetcher.type === 'done' && imagesFetcher.data) {
      setMainImage(imagesFetcher.data.productImage || imagesFetcher.data.blankImage);
      //  Handle product media_content in setSecondaryImages
      setSecondaryImages([imagesFetcher.data.productImage || imagesFetcher.data.blankImage]);
    }
  }, [imagesFetcher.data, imagesFetcher.type]);

  React.useEffect(() => {
    const props = priceFetcher.data;
    if (priceFetcher.type === 'done' && props) {
      setPrices(props.prices.price.amount);
      setPricesBreakdown(props.pricesBreakdownEstimates);
    }
  }, [priceFetcher]);

  function getProductAttributes(product: Product) {
    type ProductAttributes = {
      name?: string;
      value?: string;
    };

    function createAttribute(name: string, value: string): ProductAttributes {
      return { name: name, value: value };
    }

    const productAttributes: Array<ProductAttributes> = [];

    if (selectedColor) productAttributes.push(createAttribute('color', selectedColor));

    if (selectedSize) productAttributes.push(createAttribute('size', selectedSize));

    if (selectedBookType) productAttributes.push(createAttribute('book_type', selectedBookType));

    const packAttributes = [{ product_id: product.id, attributes: productAttributes }] as ItemAttributes[];

    return packAttributes;
  }

  function handleAddToCartClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (!quantity || imagesFetcher.type !== 'done' || !imagesFetcher.data) return;
    gtm.push_add_to_cart(product, quantity, e);

    let productCopy: Product = structuredClone(product);
    productCopy.image = mainImage;
    productCopy.blank_image = imagesFetcher.data.blankImage;

    const productAttributes = getProductAttributes(productCopy);
    fetcher.submit(
      {
        _action: 'addToCart',
        configuration_type: getConfigurationType(tab),
        packAttributes: JSON.stringify(productAttributes),
        product: JSON.stringify(productCopy),
        quantity: quantity.toString(),
      },
      { method: 'post' }
    );
  }

  function handleStartDesigning(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (!quantity || imagesFetcher.type !== 'done' || !imagesFetcher.data) return;
    gtm.push_start_design(product);

    let productCopy: Product = structuredClone(product);
    productCopy.image = mainImage;
    productCopy.blank_image = imagesFetcher.data.blankImage;

    const productAttributes = getProductAttributes(productCopy);
    submit(
      {
        _action: 'addDesignLab',
        items_attributes: JSON.stringify(productAttributes),
        last_page_comes_from: pathname?.toString(),
        product: JSON.stringify(productCopy),
        quantity: quantity.toString(),
      },
      { replace: true, method: 'post' }
    );
    onClose();
  }

  const handleChangeColor = (event: React.MouseEvent<{ value: string }>) => {
    setSelectedSize(product.available_attributes.sizes?.[0].name);
    setSelectedColor(event.currentTarget.value);
  };

  const handleChangeSize = (event: React.MouseEvent<{ value: string }>) => {
    setSelectedSize(event.currentTarget.value);
  };

  const handleBookType = (event: React.MouseEvent<{ value: string }>) => {
    setSelectedBookType(event.currentTarget.value);

    imagesFetcher.load(
      constructURLwithSearchParams(`/api/product-part-images`, product.slug, [{ book_type: event.currentTarget.value }])
    );
  };

  function handleSecondaryImageClick(image: string) {
    if (mainImage != image) {
      setMainImage(image);
    }
  }

  function handleChangeQuantity(value: number | null) {
    if (maxQuantity && value && value > maxQuantity) return;
    setQuantity(value);
  }

  function handleAttribute(attributeName: string | number, attributeProps: Attribute[]) {
    switch (attributeName) {
      case 'colors':
        return (
          <Stack key={attributeName}>
            {selectColors || tab !== 0 || !productHasMoreThanOneColorAndHasSizes ? (
              <Colors
                colors={attributeProps as Color[]}
                onColorChange={handleChangeColor}
                selectedColor={selectedColor}
                unavailableInventoryParts={product.unavailable_inventory_parts}
              />
            ) : (
              <SelectedColor colors={attributeProps as Color[]} selectedColor={selectedColor} />
            )}
          </Stack>
        );

      case 'sizes':
        return (
          <Stack key={attributeName} my={1}>
            {!selectColors || tab !== 0 ? (
              <Sizes
                onSizeChange={handleChangeSize}
                selectedSize={selectedSize}
                sizes={product.available_attributes.sizes}
                unavailableSizes={getUnavaliableSizes(product, selectedColor)}
              />
            ) : (
              <ReadOnlySize sizes={product.available_attributes.sizes} />
            )}
          </Stack>
        );
      case 'book_types':
        return (
          <Stack key={attributeName} my={1}>
            <Stack flexDirection="row" alignItems="center" flexWrap="wrap">
              <Typography variant="body2" mr={{ xs: 1, md: 0 }} fontWeight={700}>
                Types:
              </Typography>
              <Stack flexDirection="row" overflow="auto">
                {attributeProps?.map((type) => {
                  const selected = selectedBookType === type.name;

                  const selectedColor = selected ? 'text.primary' : 'primary.main';

                  return (
                    <Button
                      key={type.name}
                      variant="outlined"
                      onClick={handleBookType}
                      size="small"
                      sx={{
                        px: 1,
                        py: 0.5,
                        ml: 1,
                        minHeight: 0,
                        minWidth: 48,
                        borderColor: selectedColor,
                        color: selectedColor,
                      }}
                      name="type"
                      value={type.name}
                    >
                      {type.name}
                    </Button>
                  );
                })}
              </Stack>
            </Stack>
            <Divider sx={{ my: 2, borderColor: 'primary.main' }} />
          </Stack>
        );
    }
  }

  const isAvailable = imagesFetcher.data?.supplier_inventories?.[0]?.is_available;

  return (
    <>
      <input type="hidden" name="unitsPerPack" value={quantity ?? ''} />

      <Grid
        container
        px={{ xs: 3, md: 0 }}
        spacing={{ md: 4 }}
        flexDirection={{ xs: 'column', md: 'row' }}
        flexWrap="nowrap"
        sx={{
          position: 'relative',
          width: { xs: '100vw', md: 900 },
          height: 'fit-content',
          background: '#FFFFFF',
          boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
          borderRadius: { md: 3 },
          px: { xs: 2, md: 0 },
          pb: 3,
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={navigation.state !== 'idle' || fetcher.state !== 'idle'}
          sx={{
            width: 'fit-content',
            position: { xs: 'relative', md: 'absolute' },
            top: { md: '10px' },
            right: { xs: '-90%', md: '10px' },
            color: '#667085',
          }}
        >
          <CloseIcon />
        </IconButton>
        {/* Left side */}
        <Grid item xs={12} md={5} lg={5}>
          <Stack
            spacing={{ md: 3 }}
            px={{ xs: 3, md: 0 }}
            justifyContent="center"
            alignItems="center"
            borderRadius={2}
            height="464px"
            bgcolor="#F0F5F9"
            position="relative"
            width={{ md: '328px' }}
          >
            {isSafsiraProduct && (
              <Tooltip title="Powered with Safsira Design Lab" placement="top" arrow>
                <BoltOutlinedIcon
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'primary.main',
                    fontSize: 30,
                    '&:hover': { color: '#24598A' },
                  }}
                />
              </Tooltip>
            )}
            {mainImage && (
              <Image
                src={mainImage}
                alt="Product image"
                responsive={[{ size: { width: 280, height: 344 } }]}
                options={{ fit: 'inside' }}
                dprVariants={[1, 3]}
                width={280}
                height={344}
                style={{ maxWidth: '100%', opacity: imagesFetcher.type !== 'done' ? 0.3 : 1 }}
              />
            )}
            {imagesFetcher.type !== 'done' && (
              <MuiCircularProgress size={24} sx={{ position: 'absolute', zIndex: 1 }} />
            )}
          </Stack>
          <Grid container mt={3} ml={0} width={328} justifyContent="space-between">
            {secondaryImages.length > 4 ? (
              <Stack flexDirection="row" alignItems="center" justifyContent="space-evenly" width="100%">
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="user menu"
                  aria-haspopup="true"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  sx={{ width: 12, height: 12 }}
                >
                  <KeyboardArrowLeftOutlined />
                </IconButton>
                <Grid
                  display="flex"
                  justifyContent="start"
                  width="100%"
                  sx={{
                    '& > div:nth-of-type(n+2)': {
                      ml: 2,
                    },
                  }}
                >
                  {secondaryImages.slice((currentPage - 1) * 4, currentPage * 4).map((image) => (
                    <Stack
                      key={image + 'secondary'}
                      bgcolor="#F0F5F9"
                      borderRadius={2}
                      justifyContent="center"
                      alignItems="center"
                      py={1.25}
                      px={1.75}
                      width="60px"
                      height="60px"
                      border={image == mainImage ? `1px solid #009DC8` : '1px solid transparent'}
                      // dont compare with id because all images would have same id in the future
                      sx={{ '&:hover': { border: `1px solid #009DC8`, cursor: 'pointer' } }}
                      onClick={() => handleSecondaryImageClick(image)}
                    >
                      <Image
                        src={image || NoImageAvailable}
                        alt={product.name}
                        responsive={[{ size: { height: 40, width: 40 } }]}
                        options={{ fit: 'inside' }}
                        dprVariants={[1, 3]}
                        loading="lazy"
                        width={32}
                        height={40}
                        style={{ width: '100%', minWidth: 'auto', minHeight: 'auto', maxWidth: 122, maxHeight: 122 }}
                      />
                    </Stack>
                  ))}
                </Grid>
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="user menu"
                  aria-haspopup="true"
                  disabled={Math.ceil(secondaryImages.length / 4) === currentPage}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  sx={{ width: 12, height: 12 }}
                >
                  <KeyboardArrowRightOutlined />
                </IconButton>
              </Stack>
            ) : (
              <Grid display="flex" justifyContent="start" width="100%" sx={{ '& > div:nth-of-type(n+2)': { ml: 2 } }}>
                {secondaryImages.map((image) => (
                  <Stack
                    key={image + 'secondary'}
                    bgcolor="#F0F5F9"
                    borderRadius={2}
                    justifyContent="center"
                    alignItems="center"
                    py={1.25}
                    px={1.75}
                    width="60px"
                    height="60px"
                    border={image == mainImage ? `1px solid #009DC8` : '1px solid transparent'}
                    // dont compare with id because all images would have same id in the future
                    sx={{ '&:hover': { border: `1px solid #009DC8`, cursor: 'pointer' } }}
                    onClick={() => handleSecondaryImageClick(image)}
                  >
                    <Image
                      src={image || NoImageAvailable}
                      alt={product.name}
                      responsive={[{ size: { height: 200, width: 200 } }]}
                      options={{ fit: 'inside' }}
                      dprVariants={[1, 3]}
                      loading="lazy"
                      width={32}
                      height={40}
                      style={{ width: '100%', minWidth: 'auto', minHeight: 'auto', maxWidth: 122, maxHeight: 122 }}
                    />
                  </Stack>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item container xs={12} md={7} lg={7} direction="column">
          {/* Right side */}
          <Stack
            mr={{ md: 2 }}
            mt={{ xs: 2 }}
            maxHeight={'80vh'}
            sx={{
              overflowY: 'overlay',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#AAADB0', borderRadius: '20px' },
            }}
          >
            <Stack pl={{ md: 2 }} pr={{ md: 4 }} sx={{ '& > *': { color: '#212121' } }}>
              <Typography variant="h6" fontWeight="700">
                {product.name}
              </Typography>
              {product.description && <SeeMoreDescription description={product.description} />}
              {selectColors && tab === 0 && productHasMoreThanOneColorAndHasSizes && (
                <Typography variant="subtitle1" fontSize={14}>
                  Select one color for the product.
                </Typography>
              )}
              {!selectColors && tab === 0 && productHasMoreThanOneColorAndHasSizes && (
                <Typography variant="subtitle1" fontSize={14}>
                  Select the size, choose the quantity and add to cart. Repeat the process for every size you need.
                </Typography>
              )}
            </Stack>

            <Stack
              bgcolor="#F0F5F9"
              borderRadius={2}
              mt={2}
              mr={{ md: 2 }}
              maxHeight="60vh"
              sx={{
                overflowY: 'overlay',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#AAADB0', borderRadius: '20px' },
              }}
            >
              <Stack bgcolor="#FFFFFF" alignItems="flex-start" m={2} borderRadius={3}>
                <Grid container sx={{ m: 0, pt: 3, pb: 2, px: 2, height: '100%' }}>
                  <Stack justifyContent="start" width="100%">
                    <Stack width="100%">
                      {Object.keys(product.available_attributes).length > 0 &&
                        Object.keys(product.available_attributes).map((attribute) => {
                          return handleAttribute(attribute, product.available_attributes[attribute]);
                        })}
                    </Stack>
                  </Stack>
                </Grid>
              </Stack>
              <Stack justifyContent="center" alignItems="start" bgcolor="white" borderRadius={2} m={2} mt={0} p={2}>
                {prices ? (
                  <PriceTabs
                    product={product}
                    productPrice={prices}
                    pricesBreakdown={pricesBreakdown}
                    quantity={quantity}
                    maxQuantity={maxQuantity}
                    changeQuantity={handleChangeQuantity}
                    priceFetcher={priceFetcher}
                    color={selectedColor}
                    size={selectedSize}
                    bookType={selectedBookType}
                    tab={tab}
                    setTab={setTab}
                    isAvailable={isAvailable}
                    selectColors={selectColors}
                  />
                ) : (
                  <Stack justifyContent="center" alignItems="center" width="100%">
                    <MuiCircularProgress />
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>

          <Stack
            flexDirection={{ md: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            pr={{ md: tab === 0 ? 6 : 4 }}
            mt={1}
            gap={2}
          >
            {isSafsiraProduct ? (
              <Tooltip
                title={isAuthenticated ? '' : 'Please sign in to design and complete your purchase'}
                placement="top"
                arrow
              >
                <span style={{ width: '100%' }}>
                  <Button
                    variant="contained"
                    size="large"
                    LinkComponent={MuiRemixLink}
                    href={`/embedded-design-lab?id=${product.safsira_product_id}&slug=${product.slug}&color=${selectedColor}&qty=${quantity}`}
                    disabled={
                      priceFetcher.type !== 'done' ||
                      quantityChanged ||
                      navigation.state !== 'idle' ||
                      !canBeDesigned ||
                      !isAuthenticated
                    }
                    sx={{ width: '100%', textTransform: 'uppercase' }}
                  >
                    Start Designing
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <>
                {(!selectColors || tab !== 0 || !productHasMoreThanOneColorAndHasSizes) && (
                  <AddToCartButton
                    disabled={
                      priceFetcher.type !== 'done' ||
                      currentPart?.quantity === quantity ||
                      quantityChanged ||
                      !isAvailable
                    }
                    onClick={handleAddToCartClick}
                    cartHasProduct={Boolean(currentPart)}
                  />
                )}
                {tab === 0 && selectColors && productHasMoreThanOneColorAndHasSizes && (
                  <BuyNowButton
                    disabled={
                      priceFetcher.type !== 'done' ||
                      currentPart?.quantity === quantity ||
                      quantityChanged ||
                      !isAvailable
                    }
                    onClick={() => setSelectColors(false)}
                  />
                )}

                {tab === 0 &&
                  (selectColors || !productHasMoreThanOneColorAndHasSizes) &&
                  product.design_status?.name !== 'Approved' && (
                    <StartOrEditDesignButton
                      disabled={priceFetcher.type !== 'done' || quantityChanged || navigation.state !== 'idle'}
                      onClick={handleStartDesigning}
                      canBeDesigned={canBeDesigned}
                      editMode={Boolean(product.design_status)}
                    />
                  )}
              </>
            )}
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}

type ButtonProps = {
  disabled: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

type AddToCartButtonProps = ButtonProps & {
  cartHasProduct: boolean;
};

function AddToCartButton({ disabled, onClick, cartHasProduct }: AddToCartButtonProps) {
  return (
    <Button
      variant="contained"
      size="large"
      disabled={disabled}
      onClick={onClick}
      name="addToCart"
      sx={{ width: { xs: '100%', md: '50%' }, flex: '1 0 50%', textTransform: 'uppercase' }}
    >
      {cartHasProduct ? 'UPDATE CART' : 'ADD TO CART'}
    </Button>
  );
}
type StartDesigningButtonProps = ButtonProps & {
  canBeDesigned: boolean;
  editMode?: boolean;
};
function StartOrEditDesignButton({ disabled, onClick, canBeDesigned, editMode }: StartDesigningButtonProps) {
  const title = !canBeDesigned ? "This product can't be designed" : '';

  return (
    <Tooltip arrow placement="top" title={title}>
      <span style={{ flex: '1 0 50%', width: '100%' }}>
        <Button
          variant="contained"
          size="large"
          disabled={disabled || !canBeDesigned}
          onClick={onClick}
          sx={{ width: '100%', textTransform: 'uppercase' }}
        >
          {editMode ? 'Edit design' : 'Add to design lab'}
        </Button>
      </span>
    </Tooltip>
  );
}

function BuyNowButton({ disabled, onClick }: ButtonProps) {
  return (
    <Button
      variant="outlined"
      size="large"
      disabled={disabled}
      onClick={onClick}
      sx={{ width: { xs: '100%', md: '50%' }, flex: '1 0 50%', textTransform: 'uppercase' }}
    >
      BUY NOW
    </Button>
  );
}

function PriceTabs({
  product,
  pricesBreakdown,
  productPrice,
  quantity,
  maxQuantity,
  changeQuantity,
  priceFetcher,
  size,
  color,
  bookType,
  tab,
  setTab,
  isAvailable,
  selectColors,
}: {
  product: Product;
  pricesBreakdown: PricesBreakdownEstimates;
  productPrice: number;
  quantity: number | null;
  maxQuantity: number | undefined;
  changeQuantity: (value: number | null) => void;
  priceFetcher: FetcherWithComponents<any>;
  size: string;
  color: string;
  bookType: string;
  tab: number;
  setTab: (value: number) => void;
  isAvailable: boolean;
  selectColors: boolean;
}) {
  const [isFetchingPrice, setIsFetchingPrice] = React.useState(priceFetcher.state === 'loading');
  const packPrice = productPrice;
  const prevQuantity = priceFetcher.data?.prices.quantity ?? quantity ?? initialQuantity;
  const totalPrice = productPrice * prevQuantity;

  const isSafsiraProduct = product.safsira_connected_product;

  React.useEffect(() => {
    if (priceFetcher.state === 'loading') setIsFetchingPrice(true);
    if (priceFetcher.type === 'done') {
      setIsFetchingPrice(false);
    }
  }, [priceFetcher, packPrice]);

  const isCustom = isCustomPackOrProduct(product);

  function handleTabChange(event: React.SyntheticEvent<Element, Event>, value: any) {
    setTab(value);
    if (value === 2) {
      changeQuantity(1);
    }
  }

  return (
    <>
      <Tabs value={tab} onChange={handleTabChange} sx={{ width: '100%' }}>
        <Tab label="CUSTOM" sx={{ width: '33.33%' }} />
        {!isSafsiraProduct && (
          <Tab
            label="BLANK"
            sx={{
              width: '33.33%',
              display: isSupplierProduct(product) && product.sellable ? 'inline-flex' : 'none',
            }}
          />
        )}
        {!isSafsiraProduct && (
          <Tab
            label="SAMPLE"
            sx={{
              width: '33.33%',
              display: isSupplierProduct(product) && product.max_sample_quantity ? 'inline-flex' : 'none',
            }}
          />
        )}
      </Tabs>
      {tab === 0 && (
        <Stack width="100%">
          {!selectColors && !isSafsiraProduct && <PricesBreakdown pricesBreakdown={pricesBreakdown} />}
          <Stack
            my={3}
            sx={{
              '& > div': {
                flexDirection: 'column',
                '& > div:first-of-type': {
                  flexDirection: 'row',
                  mb: 2,
                },
                '& > div:last-of-type > div': {
                  flexDirection: 'row-reverse',
                  '&:first-of-type': {
                    mb: 2,
                  },
                },
              },
              '& hr': {
                display: 'initial',
              },
            }}
          >
            <QuantityAndPrice
              quantity={quantity}
              maxQuantity={maxQuantity}
              onChangeQuantity={changeQuantity}
              isFetchingPrice={isFetchingPrice}
              isEstimated={!isSupplierProduct(product) || !product.sellable}
              packPrice={packPrice}
              totalPrice={totalPrice}
              isAvailable={isAvailable}
              showInventoryInfo
              disableQtyinput={!isAvailable}
            />
            {/* <StartDesignOrBuildPack
              product={product}
              isDesigned={Object.keys(currentCartProduct?.mockup_images ?? {}).length > 0}
              isCustom={isCustom}
            /> */}
          </Stack>
          {!isCustom && !isSafsiraProduct && (
            <>
              <Divider style={{ marginBottom: '8px' }} />
              <Typography variant="body2">
                Don't have your own design? We'll design it for free! Price is listed based in 1 color logo and 1
                location, adding more colors may affect this price.
              </Typography>
            </>
          )}
        </Stack>
      )}
      {tab === 1 && (
        <Stack width="100%">
          <PricesBreakdown pricesBreakdown={pricesBreakdown} />

          <Stack my={3}>
            <QuantityAndPrice
              quantity={quantity}
              maxQuantity={maxQuantity}
              onChangeQuantity={changeQuantity}
              isFetchingPrice={isFetchingPrice}
              packPrice={packPrice}
              totalPrice={totalPrice}
              isAvailable={isAvailable}
              showInventoryInfo
              disableQtyinput={!isAvailable}
            />

            {/* <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
              <AddToCartButton />
            </Stack> */}
          </Stack>
          <Divider style={{ marginBottom: '8px' }} />
          <Typography variant="body2">Buy this product in blank without decoration.</Typography>
        </Stack>
      )}
      {tab === 2 && (
        <Stack width="100%">
          <Stack my={3}>
            <QuantityAndPrice
              quantity={quantity}
              maxQuantity={maxQuantity}
              onChangeQuantity={changeQuantity}
              isFetchingPrice={isFetchingPrice}
              packPrice={packPrice}
              totalPrice={totalPrice}
              isAvailable={isAvailable}
              showInventoryInfo={false}
              disableQtyinput={!isAvailable}
            />

            <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>{/* <AddToCartButton /> */}</Stack>
          </Stack>
          <Divider style={{ marginBottom: '8px' }} />
          <Typography variant="body2">
            Buy up to {product.max_sample_quantity === 1 ? '1 unit ' : `${product.max_sample_quantity} units`} of this
            product as a sample.
          </Typography>
        </Stack>
      )}
    </>
  );
}

interface Attributes {
  configuration_type: ConfigurationType;
  itemAttributes: ItemAttributes[];
  productId: number;
}

function hasCartItemSameAttributes(p: CartItem, attributes: Attributes) {
  const { productId, configuration_type, itemAttributes } = attributes;
  // check if product in cart has the same ID as product to add
  if (!(p.product.id === productId)) return false;

  // check if product in cart has the same configuration_type as product to add
  if (!(p.configuration_type === configuration_type)) return false;

  let isEqualColor: boolean;

  if (isCartItemProduct(p)) {
    const productItemAttributes = itemAttributes.find((packAttribute) =>
      packAttribute?.attributes?.some((attribute) => attribute.name === 'color')
    );
    // find product color
    const color = productItemAttributes?.attributes?.find((attribute) => attribute.name === 'color');

    // check if the product color is in a product from the cart
    isEqualColor = p.parts.some((part) =>
      part.attributes?.some((packAttribute) => packAttribute.value === color?.value)
    );
  } else {
    isEqualColor = p.parts.some((part) => {
      return part.items.every((item) => {
        const packAttribute = itemAttributes.find((packAttribute) => packAttribute.product_id === item.product_id);
        const color = packAttribute?.attributes?.find((attribute) => attribute.name === 'color');
        return item.attributes?.some((attribute) => attribute.value === color?.value);
      });
    });
  }

  return isEqualColor;
}
